import { Card, CardContent, CardHeader, Link, Typography } from "@material-ui/core";
import { debounce } from "lodash";
import React from "react";
import { ListEntryTransfer } from "../../../../model/Entry";
import { ApiBackend } from "../../../../providers/apibackend";
import { ListEntryTransfersParameters } from "../../../../providers/models";
import { vasaloppetDateTimeGetterFormatter } from "../../../../utilities/date";
import EnhancedTable from "../../../Common/EnhancedTable/EnhancedTable";
import { IColumnDefinition } from "../../../Common/EnhancedTable/models";
import SearchFilter from "../../../Common/SearchFilter/SearchFilter";
import EditEntryDialog from "../EditEntryDialog";

interface ISearchFilter {
    seller?: string;
    buyer?: string;
    status?: string;
    onlyOngoing?: boolean;
}

interface IProps { }

interface IState {
    loading: boolean;
    transfers: ListEntryTransfer[];
    searchFilter: ISearchFilter;
    editEntryId: string;
}

const isTransferred = (row: ListEntryTransfer): boolean => {
    // Ett byte är överfört när köparanmälan är kopplad eller när
    // säljaranmälan har status SOLD.
    return !!row.targetEntryId || row.sellerStatus === "SOLD";
};

const translateStatus = (row: ListEntryTransfer): string => {
    const base = row.status === "Completed" ? "Bekräftad av säljare" : "Initierad";
    return isTransferred(row) ? `${base} (övertagen)` : `${base} (pågående)`;
};

class ManageEntryTransfers extends React.Component<IProps, IState> {
    private readonly api: ApiBackend;

    constructor(props: IProps) {
        super(props);
        this.api = new ApiBackend();
        this.state = {
            loading: false,
            transfers: null,
            searchFilter: { onlyOngoing: true },
            editEntryId: null
        };
    }

    render() {
        const { searchFilter } = this.state;

        const columnDefinitions: IColumnDefinition<ListEntryTransfer>[] = [
            {
                propName: "created",
                label: "Skapad",
                valueFormatter: (row) => vasaloppetDateTimeGetterFormatter(row.created)
            },
            {
                label: "Säljare",
                sortValueGetter: (row) => `${row.sellerLastName} ${row.sellerFirstName}`,
                renderCell: (row) => (
                    <div>
                        <Link href="#" color="secondary"
                            onClick={(e: React.MouseEvent) => { e.preventDefault(); this.setState({ editEntryId: row.entryId }); }}>
                            {row.sellerFirstName} {row.sellerLastName}
                        </Link>
                        <Typography variant="body2" color="textSecondary">{row.sellerEmail || row.actionInitiatedBy}</Typography>
                    </div>
                )
            },
            {
                label: "Köpare",
                sortValueGetter: (row) => row.receiverEmail,
                renderCell: (row) => (
                    <div>
                        {(row.buyerFirstName || row.buyerLastName)
                            ? <span>{row.buyerFirstName} {row.buyerLastName}</span>
                            : <span style={{ color: "#888" }}>{isTransferred(row) ? "(övertagen)" : "(ej övertagen)"}</span>}
                        <Typography variant="body2" color="textSecondary">{row.buyerEmail || row.receiverEmail}</Typography>
                    </div>
                )
            },
            {
                propName: "productName",
                label: "Lopp"
            },
            {
                label: "Status",
                sortValueGetter: (row) => translateStatus(row),
                valueFormatter: (row) => translateStatus(row)
            },
            {
                label: "Köparen markerat betalt",
                sortValueGetter: (row) => row.buyerMarkedSellerPayed ? new Date(row.buyerMarkedSellerPayed).getTime() : 0,
                valueFormatter: (row) => row.buyerMarkedSellerPayed ? vasaloppetDateTimeGetterFormatter(row.buyerMarkedSellerPayed) : "-"
            }
        ];

        return (
            <>
                <Card>
                    <CardHeader title="Personbyten" />
                    <CardContent>
                        <SearchFilter<ISearchFilter>
                            id={"manage-entrytransfers-filter"}
                            filters={{
                                "seller": { id: "filter-seller", type: "DebouncedText", label: "Säljare (namn/e-post/telefon)", size: 4 },
                                "buyer": { id: "filter-buyer", type: "DebouncedText", label: "Köpare (namn/e-post)", size: 4 },
                                "status": {
                                    id: "filter-status", type: "GenericSelect", label: "Status", size: 2, clearable: true,
                                    itemDefinition: {
                                        itemValueType: "string",
                                        items: [
                                            { name: "Initierad", value: "Initiated" },
                                            { name: "Bekräftad", value: "Completed" }
                                        ]
                                    }
                                },
                                "onlyOngoing": { id: "filter-onlyongoing", type: "Checkbox", label: "Endast pågående", size: 2, defaultValue: true }
                            }}
                            persist={true}
                            onInit={(filter) => { this.handleSearchFilterChange(filter as unknown as ISearchFilter); }}
                            onChange={(filter) => { this.handleSearchFilterChange(filter as unknown as ISearchFilter); }}
                        />

                        <EnhancedTable<ListEntryTransfer>
                            columnDefinitions={columnDefinitions}
                            data={this.state.transfers ?? []}
                            pageSize={25}
                            page={0}
                            sortModel={{ sortBy: "created", sortOrder: "desc" }}
                            loading={this.state.loading}
                            dense
                        />
                    </CardContent>
                </Card>

                {this.state.editEntryId &&
                    <EditEntryDialog
                        entryId={this.state.editEntryId}
                        onAbortEdit={() => this.setState({ editEntryId: null })}
                        onSave={() => {
                            this.setState({ editEntryId: null });
                            this.performSearch();
                        }}
                    />
                }
            </>
        );
    }

    private handleSearchFilterChange = (searchFilter: ISearchFilter): void => {
        this.setState({ searchFilter: searchFilter, loading: true }, () => {
            this.performSearch();
        });
    };

    private performSearch = debounce(async () => {
        const { searchFilter } = this.state;
        const params: ListEntryTransfersParameters = {
            seller: searchFilter.seller,
            buyer: searchFilter.buyer,
            status: searchFilter.status,
            onlyOngoing: !!searchFilter.onlyOngoing
        };
        const transfers = await this.api.listEntryTransfers(params);
        this.setState({ transfers: transfers, loading: false });
    }, 400);
}

export default ManageEntryTransfers;
